import React from 'react'
import { Paper, Grid, TextField, Button, Typography, CircularProgress, FormControlLabel, Checkbox, Chip } from '@material-ui/core'
import { useFormik } from 'formik'
import ChipInput from 'material-ui-chip-input'
import * as Yup from 'yup'
import AddIcon from '@material-ui/icons/Add'
import HighlightOffTwoToneIcon from '@material-ui/icons/HighlightOffTwoTone'
import { useStore } from '../../context/Context'
import { postGame } from '../../api'
import { useHistory } from 'react-router-dom'
import Cookie from 'js-cookie'
import useStyles from './styles'

const GamesAdd = () => {
  const classes = useStyles()
  const history = useHistory()

  const { setIsLoading, isLoading, setListGames, listGames, setMessage } = useStore()
  const { handleSubmit, getFieldProps, errors, touched, resetForm, values, setValues } = useFormik({
    initialValues: {
      name: '',
      genre: [],
      singlePlayer: false,
      multiplayer: false,
      platform: [],
      release: 2021,
      image_url: ''
    },
    validationSchema: Yup.object({
      name: Yup.string().required('This field is a must!'),
      genre: Yup.array().min(1, 'At least one item in this field!').required(),
      singlePlayer: Yup.bool().when('multiplayer', {
        is: (multiplayer) => !multiplayer,
        then: Yup.bool().oneOf([true], 'At least one needs to be checked!')
      }),
      platform: Yup.array().min(1, 'At least one item in this field!').required(),
      release: Yup.number().typeError('Must be a number!').required('This field is a must!').min(1900).max(new Date().getFullYear()),
      image_url: Yup.string().url('Must be an image URL!').required('This field is a must!')
    }),
    onSubmit: async (values, actions) => {
      setIsLoading(true)
      const genre = values.genre.join(', ')
      const platform = values.platform.join(', ')
      const newData = { ...values, genre, platform }
      try {
        const token = await Cookie.get('token')
        const { data } = await postGame(newData, token)
        setListGames([
          ...listGames,
          data
        ])
        actions.resetForm()
        setMessage(state => ({
          ...state,
          msg: 'New game has added',
          severty: 'success',
          open: true
        }))
        history.replace('/gamesDb')
        setIsLoading(false)
      } catch (error) {
        console.log(error)
        setMessage(state => ({
          ...state,
          msg: error.message,
          severty: 'error',
          open: true,
          link: '/gamesDb/add'
        }))
        setIsLoading(false)
      }
    }
  })

  const handleAddGenreChip = (chip) => setValues({ ...values, genre: [...values.genre, chip] })
  const handleAddPlatformChip = (chip) => setValues({ ...values, platform: [...values.platform, chip] })

  const handleDeleteGenreChip = (chip) => {
    const newData = values.genre.filter(genre => genre !== chip)
    setValues({ ...values, genre: newData })
  }
  const handleDeletePlatformChip = (chip) => {
    const newData = values.platform.filter(platform => platform !== chip)
    setValues({ ...values, platform: newData })
  }

  return (
    <main className={classes.root}>
      <form onSubmit={handleSubmit} autoComplete="off">
        <Paper elevation={0} className={classes.paper} style={{ minHeight: 600 }}>
          {isLoading ? (
            <Grid container justifyContent="center" alignItems="center" className={classes.loading}>
              <CircularProgress />
            </Grid>
          ) : (
            <Grid container direction="column" spacing={3}>
              <Grid item>
                <TextField variant="outlined" label="Title" fullWidth {...getFieldProps('name')} />
                {touched.name && errors.name ? <Typography variant="caption" color="error">{errors.name}</Typography> : null}
              </Grid>
              <Grid item container direction="column" spacing={2}>
                <Grid item>
                  <ChipInput
                    value={values.genre}
                    onAdd={(chip) => handleAddGenreChip(chip)}
                    label="Genre"
                    helperText="[ Enter ] to add new item"
                    className={classes.chipInput}
                    chipRenderer={(object) => (
                      <Chip
                        label={object.value}
                        className={classes.chipGenre}
                        onDelete={() => handleDeleteGenreChip(object.value)}
                        deleteIcon={<HighlightOffTwoToneIcon style={{ color: 'white' }} />}
                      />
                    )}
                  />
                </Grid>
                <Grid item>
                  {touched.genre && errors.genre ? <Typography variant="caption" color="error">{errors.genre}</Typography> : null}
                </Grid>
              </Grid>
              <Grid item container direction="column" style={{ marginBottom: '1em' }} spacing={2}>
                <Grid item>
                  <ChipInput
                    value={values.platform}
                    onAdd={(chip) => handleAddPlatformChip(chip)}
                    label="Platform"
                    helperText="[ Enter ] to add new item"
                    className={classes.chipInput}
                    chipRenderer={(object) => (
                      <Chip
                        label={object.value}
                        className={classes.chipPlatform}
                        onDelete={() => handleDeletePlatformChip(object.value)}
                        deleteIcon={<HighlightOffTwoToneIcon style={{ color: 'white' }} />}
                      />
                    )}
                  />
                </Grid>
                <Grid item>
                  {touched.platform && errors.platform ? <Typography variant="caption" color="error">{errors.platform}</Typography> : null}
                </Grid>
              </Grid>
              <Grid item>
                <Grid item container direction="row" alignItems="center" spacing={3}>
                  <Grid item container direction="column" className={classes.checkboxes}>
                    <Grid item container>
                      <Grid item>
                        <FormControlLabel
                          control={<Checkbox className={classes.checkboxInput} {...getFieldProps('singlePlayer')} />}
                          label="Singleplayer"
                        />
                      </Grid>
                      <Grid item>
                        <FormControlLabel
                          control={<Checkbox className={classes.checkboxInput} {...getFieldProps('multiplayer')} />}
                          label="Multiplayer"
                        />
                      </Grid>

                    </Grid>
                    <Grid item>
                      {touched.singlePlayer && errors.singlePlayer ? <Typography variant="caption" color="error">{errors.singlePlayer}</Typography> : null}
                    </Grid>
                  </Grid>
                  <Grid item container direction="column" style={{ width: 'auto' }}>
                    <TextField variant="outlined" label="Released in" {...getFieldProps('release')} />
                    {touched.release && errors.release ? <Typography variant="caption" color="error">{errors.release}</Typography> : null}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <TextField multiline minRows={2} variant="outlined" label="Image URL" fullWidth {...getFieldProps('image_url')} />
                {touched.image_url && errors.image_url ? <Typography variant="caption" color="error">{errors.image_url}</Typography> : null}
              </Grid>
              <Grid item>
                <Grid item container direction="row" spacing={3}>
                  <Grid item>
                    <Button type="submit" variant="contained" className={classes.buttonSubmit}><AddIcon />&nbsp;&nbsp;Add New Game</Button>
                  </Grid>
                  <Grid item>
                    <Button onClick={resetForm} variant="outlined" className={classes.buttonReset}>Reset Form</Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Paper>
      </form>
    </main>
  )
}

export default GamesAdd
